import { getReportFooterText } from '../reportFooter';
import type { PersonLabels } from '@/hooks/usePersonLabels';
export interface DxReportHeaderInfo {
  fromDate?: string | null;
  toDate?: string | null;
}

export interface DxReportItem {
  turnInOrIssue: string;
  lin?: string | null;
  nsn?: string | null;
  nomenclature: string;
  size?: string | null;
  condition?: string | null;
  quantity?: number | null;
  date: string;
  individual?: string | null;
}

export interface DxReportTemplateOptions {
  generatedAt: Date;
  header: DxReportHeaderInfo;
  items: DxReportItem[];
  personLabels?: PersonLabels;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return sanitizeText(value);
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const buildItemsTable = (items: DxReportItem[]): string => {
  if (items.length === 0) {
    return `
      <tr>
        <td colspan="9" class="empty-row">
          No DX transactions matched the selected filters.
        </td>
      </tr>
    `;
  }

  return items
    .map(
      (item) => `
        <tr>
          <td>${sanitizeText(item.turnInOrIssue)}</td>
          <td>${sanitizeText(item.lin)}</td>
          <td>${sanitizeText(item.nsn)}</td>
          <td>${sanitizeText(item.nomenclature)}</td>
          <td>${sanitizeText(item.size)}</td>
          <td>${sanitizeText(item.condition)}</td>
          <td class="numeric">${sanitizeText(
            item.quantity !== null && item.quantity !== undefined ? String(item.quantity) : '',
          )}</td>
          <td>${sanitizeText(item.date)}</td>
          <td>${sanitizeText(item.individual)}</td>
        </tr>
      `,
    )
    .join('');
};

export const buildDxReportHtml = ({
  generatedAt,
  header,
  items,
  personLabels,
}: DxReportTemplateOptions): string => {
  const pl = personLabels ?? { label: 'Individual', labelPlural: 'Individuals', lower: 'individual', lowerPlural: 'individuals', article: 'an' as const };
  const generatedLabel = formatDateTime(generatedAt);
  const fromDate = header.fromDate ? formatDate(header.fromDate) : '—';
  const toDate = header.toDate ? formatDate(header.toDate) : '—';
  const itemsTable = buildItemsTable(items);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>DX Report</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 24px;
      }

      .brand {
        font-size: 14px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #3a82f6;
        font-weight: 600;
      }

      .title {
        font-size: 28px;
        font-weight: 700;
        margin: 0;
      }

      .generated {
        margin-top: 4px;
        font-size: 14px;
        color: #4a5c72;
      }

      .filters-row {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-top: 16px;
        margin-bottom: 16px;
      }

      .filter-block {
        min-width: 220px;
      }

      .filter-label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 2px;
      }

      .filter-value {
        font-size: 14px;
        color: #15263c;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 24px;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: center;
        font-variant-numeric: tabular-nums;
      }

      thead th.numeric {
        text-align: center;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 32px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <div class="brand">eQuip Transaction Suite</div>
      <h1 class="title">DX Report</h1>
      <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>

      <div class="filters-row">
        <div class="filter-block">
          <div class="filter-label">Start Date</div>
          <div class="filter-value">${fromDate}</div>
        </div>
        <div class="filter-block">
          <div class="filter-label">End Date</div>
          <div class="filter-value">${toDate}</div>
        </div>
      </div>
    </header>

    <section>
      <table>
        <thead>
          <tr>
            <th>Turn in / Issue</th>
            <th>LIN</th>
            <th>NSN</th>
            <th>Nomenclature</th>
            <th>Size</th>
            <th>Condition</th>
            <th class="numeric">Qty</th>
            <th>Date</th>
            <th>${pl.label}</th>
          </tr>
        </thead>
        <tbody>
          ${itemsTable}
        </tbody>
      </table>
    </section>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
